import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import MovieCard from "@/components/base/MovieCard";
import SectionHeading from "@/components/base/SectionHeading";
import EpisodeList from "./components/EpisodeList";
import PlayModal from "./components/PlayModal";
import { movies } from "@/mocks/movies";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

export default function Detail() {
  const { id = "" } = useParams();
  const movie = movies.find((m) => m.id === id);
  const [play, setPlay] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useDocumentMeta(
    movie ? `${movie.title} - 芒果tv` : "作品未找到 - 芒果tv",
    movie
      ? `${movie.title}是由${movie.director}执导的${movie.genres.join("、")}${movie.channel}，${movie.cast.join("、")}等出演。${movie.synopsis}`
      : undefined
  );

  const recommendations = useMemo(() => {
    if (!movie) return [];
    return [...movies]
      .filter((m) => m.id !== movie.id)
      .map((m) => {
        const overlap = m.tags.filter((t) => movie.tags.includes(t)).length;
        const genreOverlap = m.genres.filter((g) => movie.genres.includes(g)).length;
        const sameChannel = m.channel === movie.channel ? 1 : 0;
        return { m, score: overlap * 2 + genreOverlap + sameChannel };
      })
      .sort((a, b) => b.score - a.score || b.m.popularity - a.m.popularity)
      .slice(0, 6)
      .map((r) => r.m);
  }, [movie]);

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
    } catch {
      // ignore clipboard errors
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  if (!movie) {
    return (
      <div className="mx-auto flex max-w-7xl flex-col items-center justify-center px-4 py-24 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-error-warning-line text-3xl" />
        </span>
        <h1 className="mt-4 font-heading text-xl font-bold text-foreground-950">内容不存在</h1>
        <p className="mt-2 text-sm text-foreground-500">你访问的作品可能已被移除或链接有误。</p>
        <Link
          to="/"
          className="mt-6 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600 cursor-pointer whitespace-nowrap"
        >
          返回首页
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-10">
      <Breadcrumb
        items={[
          { label: `${movie.channel}频道`, path: `/category/${movie.channel}` },
          { label: movie.title },
        ]}
      />

      <div className="mt-4 overflow-hidden rounded-lg border border-background-200 bg-background-50">
        <div className="relative h-40 w-full overflow-hidden bg-foreground-950 md:h-56">
          <img
            src={movie.banner}
            alt={`${movie.title} 剧照`}
            className="h-full w-full object-cover object-top opacity-70"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/40 to-transparent" />
        </div>

        <div className="grid grid-cols-1 gap-6 px-5 pb-6 md:grid-cols-[260px_1fr] md:px-8 md:pb-8 lg:-mt-24">
          <div className="relative -mt-16 w-40 overflow-hidden rounded-lg border-4 border-background-50 bg-background-200 md:mt-0 md:w-full">
            <div className="aspect-[2/3] w-full">
              <img
                src={movie.cover}
                alt={`${movie.title} 海报`}
                title={`${movie.title} - 芒果tv`}
                className="h-full w-full object-cover object-top"
              />
            </div>
          </div>

          <div className="md:pt-28 lg:pt-24">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded bg-primary-500 px-2 py-0.5 text-xs font-semibold text-background-50">
                {movie.channel}
              </span>
              <span className="rounded bg-secondary-100 px-2 py-0.5 text-xs text-secondary-700">
                {movie.region}
              </span>
              <span className="rounded bg-secondary-100 px-2 py-0.5 text-xs text-secondary-700">
                {movie.year}
              </span>
            </div>

            <h1 className="mt-3 font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
              {movie.title}
            </h1>

            <div className="mt-3 flex flex-wrap items-center gap-4 text-sm">
              <span className="flex items-center gap-1 font-heading text-xl font-bold text-primary-600">
                {movie.score.toFixed(1)}
                <span className="text-xs font-normal text-foreground-500">综合热度</span>
              </span>
              <span className="text-accent-600">{movie.updateStatus}</span>
            </div>

            <div className="mt-4 space-y-1.5 text-sm text-foreground-600">
              <p>
                <span className="text-foreground-400">导演：</span>
                {movie.director}
              </p>
              <p>
                <span className="text-foreground-400">主演：</span>
                {movie.cast.join(" / ")}
              </p>
              <p>
                <span className="text-foreground-400">类型：</span>
                {movie.genres.join(" · ")}
              </p>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              {movie.tags.map((t) => (
                <span
                  key={t}
                  className="rounded-full bg-accent-50 px-3 py-1 text-xs text-accent-700"
                >
                  {t}
                </span>
              ))}
            </div>

            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600">
              {movie.synopsis}
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => setPlay(movie.channel === "电影" ? "正片" : "第1集")}
                className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-play-fill text-lg" />
                {movie.channel === "电影" ? "播放正片" : "播放第一集"}
              </button>
              <button
                type="button"
                onClick={handleShare}
                className="inline-flex items-center gap-2 rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 hover:border-primary-300 hover:text-primary-600 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-share-forward-line" />
                {copied ? "链接已复制" : "分享"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 space-y-10">
        <EpisodeList movie={movie} onPlay={(label) => setPlay(label)} />

        <section>
          <SectionHeading
            icon="ri-magic-line"
            title="相关推荐"
            subtitle="根据类型与标签为你推荐"
          />
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            {recommendations.map((m) => (
              <MovieCard key={m.id} movie={m} />
            ))}
          </div>
        </section>
      </div>

      {play && <PlayModal movie={movie} episodeLabel={play} onClose={() => setPlay(null)} />}
    </div>
  );
}